import { AnimatePresence, motion } from "framer-motion";
import { IPaymentEntry } from "../../models/epcCustomer.model";
import { Tab } from "../Tab/Tab";
import {
  approvePaymentById,
  rejectPaymentById,
} from "../../store/slices/epcSlice";
import { User } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { useState } from "react";
// import NetMeterFileCostTab from "../Cse/NetMeterFileCostTab";

export const PaymentsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onViewProofForEpc?: () => void;
}> = ({ isOpen, onClose, onConfirm }) => {
  const [activeTab, setActiveTab] = useState("epcPayment");
  const currentEpcCustomer = useSelector(
    (state: RootState) => state.epc.currentEpcCustomer,
  );

  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null,
  );
  const isPaymentApprovingLoading = useSelector(
    (state: RootState) => state.epc.loadingState.approvingPaymentLoading,
  );
  const isPaymentRejectingLoading = useSelector(
    (state: RootState) => state.epc.loadingState.rejectingPaymentLoading,
  );

  const isAnyPaymentActionLoading =
    isPaymentApprovingLoading || isPaymentRejectingLoading;

  const dispatch: RootDispatch = useDispatch();
  const handleDocumentClick = (e: React.MouseEvent, documentUrl: string) => {
    e.preventDefault();
    if (documentUrl) {
      window.open(documentUrl, "_blank");
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 text-sm font-medium rounded";
    switch (status) {
      case "approved":
        return `${baseClasses} text-emerald-700`;
      case "rejected":
        return `${baseClasses}  text-red-700`;
      default:
        return `${baseClasses} text-amber-700`;
    }
  };
  const handleApprovePaymentById = async (paymentId: string) => {
    if (isAnyPaymentActionLoading) return;

    try {
      setSelectedPaymentId(paymentId);
      await dispatch(approvePaymentById({ paymentId })).unwrap();
      onConfirm();
    } finally {
      setSelectedPaymentId(null);
    }
  };
  const handleRejectEpcPayment = async (id: string) => {
    if (isAnyPaymentActionLoading) return;

    try {
      setSelectedPaymentId(id);
      await dispatch(rejectPaymentById(id)).unwrap();
      onConfirm();
    } finally {
      setSelectedPaymentId(null);
    }
  };

  const tabs = [
    { id: "epcPayment", label: "Epc Payments", icon: User },
    // { id: "netMeterCost", label: "SR Payment", icon: FileText },
  ];
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/80"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.15 }}
          className="relative w-full max-w-6xl max-h-[95vh] bg-white border border-neutral-200 rounded-xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
            <Tab tabs={tabs} active={activeTab} setActiveTab={setActiveTab} />
            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-neutral-100 rounded-xl text-neutral-400 hover:text-neutral-600 transition-all duration-200 hover:scale-105"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div className="px-6 py-4 border-b border-neutral-200 bg-neutral-50">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-sky-600" />
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-neutral-500">
                    Customer Name
                  </p>
                  <p className="text-sm font-semibold text-neutral-900">
                    {currentEpcCustomer?.customerPayment?.customerName || "N/A"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498A1 1 0 0121 15.72V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-neutral-500">
                    Mobile Number
                  </p>
                  <p className="text-sm font-semibold text-neutral-900">
                    {currentEpcCustomer?.customerPayment
                      ?.customerMobileNumber || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {activeTab === "epcPayment" && (
            <div className="overflow-hidden">
              {currentEpcCustomer?.customerPayment?.payments?.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 bg-neutral-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-neutral-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                      />
                    </svg>
                  </div>
                  <h3 className="text-base font-medium text-neutral-900 mb-2">
                    No Payments Found
                  </h3>
                  <p className="text-sm text-neutral-500">
                    No payments have been recorded yet.
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-y-auto max-h-[60vh]">
                    <table className="w-full">
                      <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0">
                        <motion.tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                            Date & Time
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                            Via
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                            Doc Type
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                            Proof
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                            Action
                          </th>
                        </motion.tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-neutral-200">
                        {currentEpcCustomer?.customerPayment?.payments?.map(
                          (payment: IPaymentEntry, index: number) => (
                            <motion.tr
                              key={index}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: index * 0.02 }}
                              className="hover:bg-neutral-50 transition-colors"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold text-neutral-900">
                                  ₹{payment.amount.toLocaleString()}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={getStatusBadge(payment?.status!)}
                                >
                                  {payment.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-neutral-900">
                                  {new Date(
                                    payment.paymentAt,
                                  ).toLocaleDateString()}
                                </div>
                                <div className="text-xs text-neutral-500 mt-1">
                                  {new Date(
                                    payment.paymentAt,
                                  ).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-neutral-700">
                                  {payment.submittedVia || "—"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-neutral-700 uppercase">
                                  {payment.docType || "—"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {payment?.paymentProofUrl ? (
                                  <button
                                    onClick={(e) =>
                                      handleDocumentClick(
                                        e,
                                        payment.paymentProofUrl!,
                                      )
                                    }
                                    className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors"
                                  >
                                    <svg
                                      className="w-3 h-3 mr-1.5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                      />
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                      />
                                    </svg>
                                    View
                                  </button>
                                ) : (
                                  <span className="text-xs text-neutral-400">
                                    No proof
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-2">
                                  {payment.status !== "approved" && (
                                    <button
                                      disabled={isAnyPaymentActionLoading}
                                      onClick={() =>
                                        handleApprovePaymentById(payment._id)
                                      }
                                      className={`inline-flex items-center justify-center px-3 py-1.5 text-white text-xs font-medium rounded-lg transition-colors min-w-[72px] h-[28px]
    ${
      isAnyPaymentActionLoading
        ? "bg-green-400 cursor-not-allowed opacity-70"
        : "bg-green-600 hover:bg-green-700"
    }`}
                                    >
                                      {selectedPaymentId === payment._id &&
                                      isPaymentApprovingLoading ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                      ) : (
                                        "Approve"
                                      )}
                                    </button>
                                  )}
                                  {payment.status !== "rejected" && (
                                    <button
                                      disabled={isAnyPaymentActionLoading}
                                      onClick={() =>
                                        handleRejectEpcPayment(payment._id)
                                      }
                                      className={`inline-flex items-center justify-center px-3 py-1.5 text-white text-xs font-medium rounded-lg transition-colors min-w-[72px] h-[28px]
    ${
      isAnyPaymentActionLoading
        ? "bg-red-400 cursor-not-allowed opacity-70"
        : "bg-red-600 hover:bg-red-700"
    }`}
                                    >
                                      {selectedPaymentId === payment._id &&
                                      isPaymentRejectingLoading ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                      ) : (
                                        "Reject"
                                      )}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </motion.tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Footer Summary */}
                  <div className="px-6 py-4">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-neutral-500 text-xs uppercase tracking-wide mb-1">
                        Total Amount ₹
                        {currentEpcCustomer?.customerPayment?.totalBillAmount?.toLocaleString()}
                      </span>
                      <div className="flex space-x-6 text-xs">
                        <span className="text-green-600 font-medium">
                          {
                            currentEpcCustomer?.customerPayment?.payments?.filter(
                              (p) => p.status === "approved",
                            ).length
                          }{" "}
                          Approved
                        </span>
                        <span className="text-orange-600 font-medium">
                          {
                            currentEpcCustomer?.customerPayment?.payments?.filter(
                              (p) => p.status === "pending",
                            ).length
                          }{" "}
                          Pending
                        </span>
                        <span className="text-red-600 font-medium">
                          {
                            currentEpcCustomer?.customerPayment?.payments?.filter(
                              (p) => p.status === "rejected",
                            ).length
                          }{" "}
                          Rejected
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* <div className="px-9 py-2">
            {activeTab === "netMeterCost" && (
              <NetMeterFileCostTab
                customer={currentEpcCustomer.customerPayment!}
                isPaymentDoneOnSalesRep={
                  currentEpcCustomer?.customerData?.isPaymentDoneOnSalesRep!
                }
                customerId={currentEpcCustomer?.customerData?._id!}
                setShowPaymentsModal={onClose}
              />
            )}
          </div> */}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
